import { useState, useEffect } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { loginRequest } from "../auth/authConfig";
import { jwtDecode } from "jwt-decode";
import { TokenClaims } from "../helpers/checkUserAuthorization";

const useAuth = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [isInitialized, setIsInitialized] = useState(false);
  const [role, setRole] = useState<string[]>();
  const isAuthenticated = accounts.length > 0 && inProgress === "none";

  useEffect(() => {
    if (inProgress === "none") {
      setIsInitialized(true);
    }
  }, [inProgress]);

  useEffect(() => {
    const getRole = async () => {
      if (isInitialized && accounts.length > 0) {
        const token = await getToken();
        if (token) {
          const decoded: TokenClaims = jwtDecode(token);
          setRole(decoded.roles ?? []);
        }
      }
    };
    getRole();
  }, [isInitialized, accounts.join()]);

  const getToken = async (): Promise<string | null> => {
    if (accounts.length === 0) return null;

    try {
      const silentResult = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });
      return silentResult.accessToken;
    } catch (error: any) {
      console.warn(
        "Silent token acquisition failed, falling back to interactive",
        error
      );

      if (error instanceof InteractionRequiredAuthError) {
        try {
          const interactiveResult = await instance.acquireTokenPopup({
            ...loginRequest,
            account: accounts[0],
          });
          return interactiveResult.accessToken;
        } catch (popupError) {
          console.error(
            "Interactive token acquisition (popup) failed",
            popupError
          );
          return null;
        }
      } else {
        console.error("Token acquisition failed", error);
        return null;
      }
    }
  };

  const { data: token, isLoading } = useQuery({
    queryKey: ["token"],
    queryFn: getToken,
    enabled: isInitialized && isAuthenticated,
    staleTime: 30 * 60 * 1000,
  });

  return { token, role, isLoading, isInitialized, isAuthenticated };
};

export default useAuth;
